// https://drboolean.gitbooks.io/mostly-adequate-guide/content/ch5.html
// tslint:disable:ordered-imports unnecessary-else
import { ActionReducer, combineReducers } from '@ngrx/store';
import { auditPropertiesReducer } from './middleware/audit-properties-reducer';
import { ENVIRONMENTS } from '../../environments/environments';

import { compose } from '@ngrx/core/compose';
import { storeFreeze } from 'ngrx-store-freeze';
import { resetReducer } from '../../core/store/reset-state/reset.reducer';

import * as fromAccessGroup from '../business-objects/access-group/access-group.reducer';
import * as fromAccount from '../business-objects/account/account.reducer';
import * as fromAccountStatistics from '../business-objects/account-statistics/account-statistics.reducer';
import * as fromCalendar from '../pages/job-calendar/calendar.reducer';
import * as fromCategoryGroup from '../business-objects/category-group/category-group.reducer';
import * as fromConfig from '../business-objects/config/config.reducer';
import * as fromContact from '../business-objects/contact/contact.reducer';
import * as fromContactType from '../business-objects/contact-type/contact-type.reducer';
import * as fromControl from '../business-objects/control/control.reducer';
import * as fromControlType from '../business-objects/control-type/control-type.reducer';
import * as fromDefaultTask from '../business-objects/default-task/default-task.reducer';
import * as fromEmailAttachmentPickerModal from '../../app/modals-popovers/email-attachment-picker-modal/email-attachment-picker-modal.reducer';
import * as fromFile from '../business-objects/file/file.reducer';
import * as fromFileManager from '../../app/molecules/file-manager/file-manager.reducer';
import * as fromForm from '../business-objects/form/form.reducer';
import * as fromFormManager from '../../app/pages/form-manager-page/form-manager-page.reducer';
import * as fromGroup from '../business-objects/group/group.reducer';
import * as fromJob from '../business-objects/job/job.reducer';
import * as fromJobListPage from '../../app/pages/job-list-page/job-list-page.reducer';
import * as fromJobExport from '../../app/pages/job-export-page/job-export.reducer';
import * as fromJobPrint from '../../app/pages/job-print/job-print.reducer';
import * as fromJobStatus from '../business-objects/job-status/job-status.reducer';
import * as fromJobStatusPicker from '../../app/atoms/job-status-picker-popover/job-status-picker-popover.reducer';
import * as fromJobStatusSummary from '../business-objects/job-status-summary-item/job-status-summary.reducer';
import * as fromJobTask from '../business-objects/task/task.reducer';
import * as fromJobTaskType from '../business-objects/task-type/task-type.reducer';
import * as fromJobType from '../business-objects/job-type/job-type.reducer';
import * as fromLookup from '../business-objects/lookup/lookup.reducer';
import * as fromLookupType from '../business-objects/lookup-type/lookup-type.reducer';
import * as fromMediaPickerModal from '../../app/modals-popovers/media-picker-modal/media-picker-modal.reducer';
import * as fromMembership from '../business-objects/membership/membership.reducer';
import * as fromNetwork from '../../core/business-objects/network/network.reducer';
import * as fromNote from '../business-objects/note/note.reducer';
import * as fromNotificationTemplate from '../business-objects/notification-template/notification-template.reducer';
import * as fromReport from '../business-objects/report/report.reducer';
import * as fromReportLayout from '../business-objects/report-layout/report-layout.reducer';
import * as fromSearchAsYouType from '../../app/atoms/search-as-you-type/search-as-you-type.reducer';
import * as fromSyncUpItem from '../business-objects/sync-up-item/sync-up-item.reducer';
import * as fromTextControl from '../controls/text/form/text-control.reducer';
import * as fromTextControlMobileModal from '../../app/modals-popovers/text-control-mobile-modal/text-control-mobile-modal.reducer';
import * as fromTransition from '../business-objects/transition/transition.reducer';
import * as fromUser from '../business-objects/user/user.reducer';
import * as fromContactPickerPage from '../pages/contact-picker/contact-picker.reducer';
import { isInArray } from '../../core/util/is-in-array';
import { jobTypeReducer } from './middleware/job-type-reducer';
import { ENV } from '../../environments/environment';

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface State {
    accessGroup?: fromAccessGroup.State;
    account?: fromAccount.State;
    accountStatistics?: fromAccountStatistics.State;
    calendar?: fromCalendar.State;
    categoryGroup?: fromCategoryGroup.State;
    config?: fromConfig.State;
    contact?: fromContact.State;
    contactPickerPage?: fromContactPickerPage.ContactPickerState;
    contactType?: fromContactType.State;
    control?: fromControl.State;
    controlType?: fromControlType.State;
    defaultTask?: fromDefaultTask.State;
    emailAttachmentPickerModal?: fromEmailAttachmentPickerModal.State;
    file?: fromFile.State;
    fileManager?: fromFileManager.State;
    form?: fromForm.State;
    formManager?: fromFormManager.State;
    group?: fromGroup.State;
    job?: fromJob.State;
    jobsExportPage?: fromJobExport.State;
    jobListPage?: fromJobListPage.State;
    jobPrint?: fromJobPrint.State;
    jobStatus?: fromJobStatus.State;
    jobStatusPicker?: fromJobStatusPicker.State;
    jobStatusSummary?: fromJobStatusSummary.State;
    jobType?: fromJobType.State;
    lookup?: fromLookup.State;
    lookupType?: fromLookupType.State;
    mediaPickerModal?: fromMediaPickerModal.State;
    membership?: fromMembership.State;
    network?: fromNetwork.State;
    note?: fromNote.State;
    notificationTemplate?: fromNotificationTemplate.State;
    report?: fromReport.State;
    reportLayout?: fromReportLayout.State;
    searchAsYouType?: fromSearchAsYouType.State;
    syncUpItem?: fromSyncUpItem.State;
    task?: fromJobTask.State;
    taskType?: fromJobTaskType.State;
    textControl?: fromTextControl.State;
    textControlMobileModal?: fromTextControlMobileModal.State;
    transition?: fromTransition.State;
    user?: fromUser.State;
}

export const initialStates = {
    accessGroup: fromAccessGroup.initialState,
    account: fromAccount.initialState,
    accountStatistics: fromAccountStatistics.initialState,
    calendar: fromCalendar.initialState,
    categoryGroup: fromCategoryGroup.initialState,
    config: fromConfig.initialState,
    contact: fromContact.initialState,
    contactPickerPage: fromContactPickerPage.initialState,
    contactType: fromContactType.initialState,
    control: fromControl.initialState,
    controlType: fromControlType.initialState,
    defaultTask: fromDefaultTask.initialState,
    emailAttachmentPickerModal: fromEmailAttachmentPickerModal.initialState,
    file: fromFile.initialState,
    fileManager: fromFileManager.initialState,
    form: fromForm.initialState,
    formManager: fromFormManager.initialState,
    group: fromGroup.initialState,
    job: fromJob.initialState,
    jobListPage: fromJobListPage.initialState,
    jobsExportPage: fromJobExport.initialState,
    jobPrint: fromJobPrint.initialState,
    jobStatus: fromJobStatus.initialState,
    jobStatusPicker: fromJobStatusPicker.initialState,
    jobStatusSummary: fromJobStatusSummary.initialState,
    jobType: fromJobType.initialState,
    lookup: fromLookup.initialState,
    lookupType: fromLookupType.initialState,
    mediaPickerModal: fromMediaPickerModal.initialState,
    membership: fromMembership.initialState,
    network: fromNetwork.initialState,
    note: fromNote.initialState,
    notificationTemplate: fromNotificationTemplate.initialState,
    report: fromReport.initialState,
    reportLayout: fromReportLayout.initialState,
    searchAsYouType: fromSearchAsYouType.initialState,
    syncUpItem: fromSyncUpItem.initialState,
    task: fromJobTask.initialState,
    taskType: fromJobTaskType.initialState,
    textControl: fromTextControl.initialState,
    textControlMobileModal: fromTextControlMobileModal.initialState,
    transition: fromTransition.initialState,
    user: fromUser.initialState
};

export interface BaseObjectFilters {
    searchTerm?: string;
    sortBy?: string;
    sortDirection?: number;
}

export interface PaginatableObjectFilters extends BaseObjectFilters {
    limit?: number;
    offset?: number;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const reducers = {
    accessGroup: fromAccessGroup.reducer,
    account: fromAccount.reducer,
    accountStatistics: fromAccountStatistics.reducer,
    calendar: fromCalendar.reducer,
    categoryGroup: fromCategoryGroup.reducer,
    config: fromConfig.reducer,
    contact: fromContact.reducer,
    contactPickerPage: fromContactPickerPage.reducer,
    contactType: fromContactType.reducer,
    control: fromControl.reducer,
    controlType: fromControlType.reducer,
    defaultTask: fromDefaultTask.reducer,
    emailAttachmentPickerModal: fromEmailAttachmentPickerModal.reducer,
    file: fromFile.reducer,
    fileManager: fromFileManager.reducer,
    form: fromForm.reducer,
    formManager: fromFormManager.reducer,
    group: fromGroup.reducer,
    job: fromJob.reducer,
    jobListPage: fromJobListPage.reducer,
    jobsExportPage: fromJobExport.reducer,
    jobPrint: fromJobPrint.reducer,
    jobStatus: fromJobStatus.reducer,
    jobStatusPicker: fromJobStatusPicker.reducer,
    jobStatusSummary: fromJobStatusSummary.reducer,
    jobType: fromJobType.reducer,
    lookup: fromLookup.reducer,
    lookupType: fromLookupType.reducer,
    mediaPickerModal: fromMediaPickerModal.reducer,
    membership: fromMembership.reducer,
    network: fromNetwork.reducer,
    note: fromNote.reducer,
    notificationTemplate: fromNotificationTemplate.reducer,
    report: fromReport.reducer,
    reportLayout: fromReportLayout.reducer,
    searchAsYouType: fromSearchAsYouType.reducer,
    syncUpItem: fromSyncUpItem.reducer,
    task: fromJobTask.reducer,
    taskType: fromJobTaskType.reducer,
    textControl: fromTextControl.reducer,
    textControlMobileModal: fromTextControlMobileModal.reducer,
    transition: fromTransition.reducer,
    user: fromUser.reducer
};

const developmentReducer: ActionReducer<State> = compose(
    resetReducer,
    jobTypeReducer,
    auditPropertiesReducer,
    storeFreeze,
    combineReducers
)(reducers);

const productionReducer: ActionReducer<State> = compose(
    resetReducer,
    jobTypeReducer,
    auditPropertiesReducer,
    combineReducers
)(reducers);

// tslint:disable-next-line:only-arrow-functions
export function reducer(state: any, action: any): State {
    if (isInArray(ENV.name, [ENVIRONMENTS.PROD, ENVIRONMENTS.E2E])) {
        return productionReducer(state, action);
    } else {
        return developmentReducer(state, action);
    }
}



// WEBPACK FOOTER //
// ./src/vas/store/bootstrap.ts